import styled, { keyframes } from "styled-components"

// Fade and slide animation for logo
const fadeInSlideDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

// Zoom-in animation for background
const zoomIn = keyframes`
  0% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

export const PageContainer = styled.main`
  display: grid;
  grid-template-columns: minmax(360px, 0.9fr) minmax(520px, 1.1fr);
  min-height: 100svh;
  background: #071a2e;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    background: #eef3f7;
  }
`

export const VisualSection = styled.img`
  position: sticky;
  top: 0;
  animation: ${zoomIn} 0.8s ease-out;
  width: 100%;
  height: 100svh;
  object-fit: cover;
  filter: saturate(0.78) brightness(0.78);

  @media (max-width: 960px) {
    position: relative;
    height: 280px;
    filter: saturate(0.78) brightness(0.72);
  }
`

export const Logo = styled.a`
  position: fixed;
  z-index: 5;
  top: 1.25rem;
  left: 1.25rem;
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(170, 194, 212, 0.7);
  box-shadow: 0 16px 35px rgba(0, 17, 33, 0.16);

  svg {
    max-width: 160px;
    width: 100%;
    height: auto;
    max-height: 72px;
    margin: 0;
    animation: ${fadeInSlideDown} 0.45s ease-out forwards;
  }

  @media (max-width: 960px) {
    position: absolute;
    top: 1rem;
    left: 1rem;

    svg {
      width: 120px;
    }
  }
`

export const Logo2 = styled.a`
  position: relative;
  top: 0.5rem;
  left: 0rem;
  display: none;
  align-items: center;
  font-size: 1.8rem;
  font-weight: bold;
  color: #007bff;
  text-decoration: none;
  padding: 0 20px;

  svg {
    max-width: 170px;
    width: 100%;
    height: auto;
    max-height: 100px;
    margin: 0 auto;
    margin-top: 0.85rem;
    animation: ${fadeInSlideDown} 0.6s ease-out forwards; /* Logo animation */
    animation-delay: 0.8s;
    opacity: 0;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 200px;
    }
  }
`

export const FormSection = styled.div`
  align-self: center;
  width: min(680px, calc(100% - 3rem));
  max-height: calc(100svh - 3rem);
  overflow: auto;
  background: #ffffff;
  border-radius: 2px;
  padding: clamp(1.5rem, 5vw, 3.4rem);
  margin: 1.5rem auto;
  border-top: 4px solid #337abd;
  box-shadow: 0 30px 80px rgba(0, 12, 28, 0.32);

  @media (max-width: 960px) {
    max-height: none;
    margin: -54px auto 3rem;
    position: relative;
    z-index: 2;
    box-shadow: 0 24px 60px rgba(15, 43, 68, 0.16);
  }
`

export const Title = styled.h1`
  max-width: 580px;
  margin: 0;
  color: #102f4f;
  font-size: clamp(2.1rem, 5vw, 3.7rem);
  line-height: 0.98;
  letter-spacing: -0.05em;
`

export const FormEyebrow = styled.p`
  margin: 0 0 0.7rem;
  color: #337abd;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`

export const FormIntro = styled.p`
  margin: 1rem 0 1.8rem;
  color: #617587;
  font-size: 0.95rem;
  line-height: 1.65;
`

export const InputGroup = styled.div`
  margin-bottom: 1.35rem;
`

export const Label = styled.label`
  color: #193a55;
  font-size: 0.82rem;
  font-weight: 750;
  margin-bottom: 0.5rem;
  display: block;
`

export const Input = styled.input`
  width: 100%;
  min-height: 50px;
  padding: 0 0.8rem;
  border: 1px solid #bac8d3;
  border-radius: 2px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #337abd;
    box-shadow: 0 0 0 4px rgba(51, 122, 189, 0.12);
  }
`

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  min-height: 115px;
  border: 1px solid #bac8d3;
  border-radius: 2px;
  font-size: 1rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #337abd;
    box-shadow: 0 0 0 4px rgba(51, 122, 189, 0.12);
  }
`

export const PresetButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

export const PresetButton = styled.button`
  min-height: 38px;
  padding: 0.4rem 0.8rem;
  background: ${({ $isActive }) => ($isActive ? "#1d6da6" : "#f1f5f8")};
  color: ${({ $isActive }) => ($isActive ? "white" : "#333")};
  border: 1px solid ${({ $isActive }) => ($isActive ? "#1d6da6" : "#c5d1da")};
  border-radius: 2px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1d6da6;
    color: white;
  }

  &:focus {
    outline: none;
  }
`

export const SubmitButton = styled.button`
  width: 100%;
  min-height: 54px;
  padding: 0 1.1rem;
  background: linear-gradient(135deg, #337abd, #175787);
  color: white;
  border: none;
  border-radius: 2px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.primaryDark};
  }

  &:disabled {
    background-color: #6c757d;
  }

  ${({ empty, theme }) =>
    empty &&
    `
    background:none;
    color: ${theme.primary};
    border: 1.5px solid ${theme.primary};
  `}
`

export const Error = styled.div`
  margin-top: 0.3rem;
  color: #a43c2e;
  font-size: 0.78rem;
`

export const SuccessActions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0.75rem 0 1.25rem;
`

const BaseLinkButton = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
  padding: 0.85rem 1.25rem;
  border-radius: 2px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`

export const PrimaryLinkButton = styled(BaseLinkButton)`
  background-color: ${({ theme }) => theme.primary};
  color: white;
  border: none;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);

  &:hover {
    background-color: ${({ theme }) => theme.primaryDark};
  }
`

export const SecondaryLinkButton = styled(BaseLinkButton)`
  background: white;
  color: ${({ theme }) => theme.primary};
  border: 1.5px solid ${({ theme }) => theme.primary};

  &:hover {
    background: rgba(0, 123, 255, 0.04);
  }
`

// export const GridRow = styled(GridRowRef)`
//   display: grid;
//   grid-template-columns: 1fr 1fr;
//   gap: 1rem;
//   margin: 0;
// `

export const GridRow = styled.div`
  display: grid;
  gap: 1rem;

  /* Custom column ratios for desktop */
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr"};

  @media (max-width: 620px) {
    grid-template-columns: 1fr; /* Single-column for mobile */
  }
`

export const TrustList = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  margin: 1.4rem 0 1.8rem;
  padding: 0;
  border-top: 1px solid #d2dce4;
  border-bottom: 1px solid #d2dce4;
  list-style: none;

  li {
    padding: 0.8rem 0.65rem;
    color: #526b7e;
    font-size: 0.72rem;
    line-height: 1.4;
  }

  li + li {
    border-left: 1px solid #d2dce4;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;

    li + li {
      border-top: 1px solid #d2dce4;
      border-left: 0;
    }
  }
`

export const DirectContact = styled.p`
  margin: 1rem 0 0;
  color: #6d7f8e;
  font-size: 0.76rem;
  line-height: 1.5;

  a {
    color: #276897;
    font-weight: 750;
    text-decoration: none;
  }
`
